import { useState, useEffect, useCallback } from 'react';
import Loader from 'react-loader-spinner';
import { useAuth } from '~/auth';
import { NextLink, Translate } from '~/i18n';
import Axios from '~/utils/axios';
import { VoucherData } from '~/interfaces/vouchers';
import { Help, Plus } from '~/svgs/common';
import { VoucherStar } from '~/svgs/vouchers';
import VoucherBalance from './balance';
import VoucherSection from './voucher-section';
import BalanceHistory from './balance-history';
import cn from 'classnames';
import { profileVouchersTabBuyBtnEvent } from '~/utils/mixpanel-events/profile';
import { sentryCaptureException } from '~/errors/sentry';

/**
 * This page contains user voucher credit details: voucher balance, active
 * vouchers, redeemed vouchers, and balance history
 */
const VouchersContent = () => {
   const [loading, setLoading] = useState(true);
   const { authenticated, user, updateUser } = useAuth();
   const [voucherData, setVoucherData] = useState<VoucherData>({
      user_balance: 0,
      active_vouchers: [],
      redeemed_vouchers: [],
      balance_history: [],
   });

   useEffect(() => {
      const getBalance = async () => {
         try {
            const { data } = await Axios.get('/balance-info');

            setVoucherData({
               ...data.data,
               balance_history: data.data.history.data,
            });
         } catch (error) {
            sentryCaptureException(error);
         } finally {
            setLoading(false);
         }
      };

      if (authenticated && user) getBalance();
   }, [authenticated, user]);

   const handleRedeem = async (code: string) => {
      try {
         const { data } = await Axios.post('/vouchers/redeem', {
            code,
            with_history: true,
         });

         if (data.status === 'Success') {
            setVoucherData({
               ...data.data,
               balance_history: data.data.history.data,
            });
            updateUser({ ...user, balance: data.data.user_balance });
         }
      } catch (error) {
         sentryCaptureException(error);
      }
   };

   // Callback function to child to update vouchers data
   const updateVouchersData = useCallback(
      ({ user_balance, active_vouchers, redeemed, balance_history }) => {
         setVoucherData(prev => ({
            user_balance,
            active_vouchers,
            redeemed_vouchers: [redeemed, ...prev.redeemed_vouchers],
            balance_history,
         }));
         updateUser({ ...user, balance: user_balance });
      },
      [user, updateUser]
   );

   if (loading)
      return (
         <div className="flex items-center justify-center mt-12">
            <Loader type="Puff" color="#212529" width={40} height={40} />
         </div>
      );

   return (
      <div>
         <div className="md:w-1/2">
            {/* Header section */}
            <div className="flex">
               <span className="flex-grow text-center md:text-lg md:font-bold md:text-left md:rtl:text-right">
                  <Translate id="vouchers:my vouchers" />
               </span>
               <NextLink href="/vouchers/explain-vouchers">
                  <Help />
               </NextLink>
            </div>
            {/* Balance */}
            <div className="py-4">
               <VoucherBalance
                  userBalance={voucherData.user_balance}
                  updateVouchersData={updateVouchersData}
               />
            </div>
         </div>

         <div>
            <div className="text-gray-deminished bg-gray-light rounded-2xl">
               {/* Active Vouchers */}
               <div className="p-4">
                  <div
                     className={cn(
                        voucherData.active_vouchers.length > 0 && 'flex justify-between'
                     )}>
                     <div className="mb-3">
                        <Translate id="vouchers:active" />
                     </div>
                     {/* Buy Vouchers */}
                     <NextLink
                        href="/vouchers"
                        onClick={profileVouchersTabBuyBtnEvent}
                        className={cn(
                           'flex justify-center text-gray-700 text-xs',
                           voucherData.active_vouchers.length === 0
                              ? 'bg-white shadow-base focus:ring hover:bg-gray-100 hover:bg-opacity-25 items-center mx-auto my-12 px-10 py-2.5 rounded-xl w-40 whitespace-nowrap'
                              : 'pt-1'
                        )}>
                        <div className="ltr:mr-1 rtl:ml-1">
                           <Plus />
                        </div>
                        <div>
                           <Translate id="vouchers:buy voucher" />
                        </div>
                     </NextLink>
                  </div>
                  {/* Swiper Component */}
                  {voucherData.active_vouchers.length > 0 && (
                     <VoucherSection
                        active={true}
                        vouchers={voucherData.active_vouchers}
                        handleRedeem={handleRedeem}
                     />
                  )}
               </div>
               {/* Redeemed Vouchers */}
               <div
                  className={cn(
                     'p-4',
                     voucherData.redeemed_vouchers.length === 0 && 'flex flex-col'
                  )}>
                  <div className="mb-3">
                     <Translate id="vouchers:redeemed" />
                  </div>
                  {voucherData.redeemed_vouchers.length > 0 ? (
                     // Swiper Component
                     <VoucherSection
                        active={false}
                        vouchers={voucherData.redeemed_vouchers}
                        handleRedeem={handleRedeem}
                     />
                  ) : (
                     // No Redeemed Vouchers
                     <div className="bg-white flex flex-col flex-grow h-36 justify-center items-center my-4 rounded-md space-y-3.5 text-center text-gray-400 text-sm">
                        <div>
                           <VoucherStar />
                        </div>
                        <div>
                           <Translate id="vouchers:no redeemed vouchers" />
                        </div>
                     </div>
                  )}
               </div>
               {/* Balance History */}
               <BalanceHistory balance_history={voucherData.balance_history} />
            </div>
         </div>
      </div>
   );
};

export default VouchersContent;
